// src/components/pages/ProfileForm.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaUser, FaPhone, FaMapMarkerAlt, FaSpinner, FaCheck, 
  FaArrowLeft, FaCamera, FaUserCircle
} from 'react-icons/fa';
import { updateProfile, getProfile, setProfileCompleted, updateUserName, setShowLoginModal } from '../../redux/slices/authSlice';

const ProfileForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { isLoading, phone, user, isAuthenticated } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    profile_image: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated && !localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    
    // Pre-fill form if user has data
    if (user?.name && user.name !== 'User' && user.name !== 'Account') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        address: user.address || '',
      }));
      if (user?.profile_image) {
        setPreviewImage(`https://admin.thefoodies.in/${user.profile_image}`);
      }
    }
  }, [user, isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      setFormData(prev => ({ ...prev, profile_image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return;
    }

    setIsUploading(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('phone', phone);
      submitData.append('address', formData.address.trim() || '');
      
      if (formData.profile_image) {
        submitData.append('profile_image', formData.profile_image);
      }

      const response = await dispatch(updateProfile(submitData)).unwrap();
      console.log('✅ Profile updated:', response);

      if (response.status) {
        setSuccess(true);
        
        // ✅ Step 1: Fetch updated profile from server
        const profileResult = await dispatch(getProfile()).unwrap();
        console.log('✅ Fresh profile fetched:', profileResult);
        
        // ✅ Step 2: Get the actual name from response
        const profileData = profileResult?.data || profileResult;
        const actualName = profileData?.name || formData.name.trim();
        
        // ✅ Step 3: Update Redux state with the actual name
        dispatch(updateUserName(actualName));
        dispatch(setProfileCompleted(true));
        
        // ✅ Step 4: Clear new user flag
        localStorage.setItem('isNewUser', 'false');
        localStorage.setItem('profileCompleted', 'true');
        
        // ✅ Step 5: Update user data in localStorage with the actual name
        const userData = {
          id: user?.id || response?.data?.id || profileData?.id,
          name: actualName,
          phone: phone,
          address: formData.address.trim() || '',
          profile_image: profileData?.profile_image || response?.data?.profile_image || null,
          is_new_user: false
        };
        localStorage.setItem('user', JSON.stringify(userData));
        
        // ✅ Step 6: Update auth login data
        const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
        authUser.name = actualName;
        authUser.profile_completed = true;
        authUser.is_new_user = false;
        localStorage.setItem('tiffinUserLogin', JSON.stringify(authUser));
        
        console.log('✅ User data saved with name:', actualName);
        console.log('✅ localStorage user:', JSON.parse(localStorage.getItem('user') || '{}'));
        
        // ✅ Step 7: Dispatch events to notify navbar
        window.dispatchEvent(new CustomEvent('profileUpdated', { 
          detail: { 
            name: actualName,
            phone: phone,
            address: formData.address.trim() || '' 
          } 
        }));
        window.dispatchEvent(new Event('storage'));
        
        // ✅ Step 8: Close any login modals and redirect to Home Page
        dispatch(setShowLoginModal(false));
        
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('❌ Profile update failed:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkip = () => {
    // If user skips, use phone number as name
    const defaultName = phone || 'User';
    dispatch(updateUserName(defaultName));
    dispatch(setProfileCompleted(true));
    localStorage.setItem('isNewUser', 'false');
    localStorage.setItem('profileCompleted', 'true');
    
    const userData = {
      id: user?.id,
      name: defaultName,
      phone: phone,
      address: '',
      is_new_user: false
    };
    localStorage.setItem('user', JSON.stringify(userData));
    
    window.dispatchEvent(new Event('storage'));
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: colors.background }}>
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium mb-4 transition-all hover:scale-105"
          style={{ color: colors.primary }}
        >
          <FaArrowLeft className="text-xs" /> Back
        </button>

        <div className="text-center mb-6">
          <div className="text-5xl mb-3">👋</div>
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            Welcome to Foodies!
          </h1>
          <p className="mt-1" style={{ color: colors.textSecondary }}>
            Complete your profile to get started
          </p>
        </div>

        <div 
          className="rounded-2xl p-6"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* Phone Display */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Phone Number
              </label>
              <div 
                className="flex items-center gap-2 p-3 rounded-xl"
                style={{ 
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.border}`
                }}
              >
                <FaPhone style={{ color: colors.primary }} />
                <span className="font-medium" style={{ color: colors.text }}>{phone || user?.phone || 'Not available'}</span>
                <FaCheck className="ml-auto text-green-500" />
              </div>
            </div>

            {/* Profile Image Upload */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Profile Photo
              </label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {previewImage ? (
                    <img 
                      src={previewImage} 
                      alt="Profile preview" 
                      className="w-20 h-20 rounded-full object-cover border-2"
                      style={{ borderColor: colors.primary }}
                    />
                  ) : (
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
                      style={{ 
                        backgroundColor: colors.inputBackground,
                        border: `2px dashed ${colors.border}`
                      }}
                    >
                      <FaUserCircle style={{ color: colors.textSecondary }} />
                    </div>
                  )}
                  <label 
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-105"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <FaCamera className="text-white text-sm" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div>
                  <p className="text-sm" style={{ color: colors.text }}>
                    Upload a profile photo
                  </p>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>
                    JPG, PNG or GIF (max 2MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Name Input */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ 
                backgroundColor: colors.inputBackground,
                border: `1px solid ${error ? '#EF4444' : colors.border}`
              }}>
                <FaUser style={{ color: colors.primary }} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="flex-1 bg-transparent focus:outline-none"
                  style={{ color: colors.text }}
                  autoFocus
                />
              </div>
            </div>

            {/* Address Input */}
            <div className="mb-6">
              <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                Delivery Address
              </label>
              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ 
                backgroundColor: colors.inputBackground,
                border: `1px solid ${colors.border}`
              }}>
                <FaMapMarkerAlt style={{ color: colors.primary }} />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your delivery address"
                  className="flex-1 bg-transparent focus:outline-none"
                  style={{ color: colors.text }}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                This will be used for delivering your tiffin orders
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-xl mb-4 text-sm" style={{ 
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FECACA'
              }}>
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="p-3 rounded-xl mb-4 text-sm flex items-center gap-2" style={{ 
                backgroundColor: '#F0FDF4',
                color: '#16A34A',
                border: '1px solid #BBF7D0'
              }}>
                <FaCheck /> Profile created successfully! Redirecting to home...
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isUploading || isLoading}
              className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              style={{
                backgroundColor: colors.primary,
                boxShadow: `0 4px 20px ${colors.primary}40`,
                opacity: isUploading || isLoading ? 0.7 : 1
              }}
            >
              {isUploading || isLoading ? (
                <>
                  <FaSpinner className="animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <FaCheck /> Complete Profile
                </>
              )}
            </button>

            {/* Skip Button */}
            <button
              type="button"
              onClick={handleSkip}
              className="w-full mt-3 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02]"
              style={{ 
                backgroundColor: colors.inputBackground,
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`
              }}
            >
              Skip for now
            </button>
          </form>

          <p className="text-xs text-center mt-4" style={{ color: colors.textSecondary + '80' }}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileForm;